import React from 'react';
import { Card, CardContent } from '@/components/ui/Card';
import { PrMetricsSummary } from '@/types/domain/pr-management';
import { Layers, Clock, CheckCircle2, AlertTriangle, ShieldCheck, DollarSign } from 'lucide-react';

interface PrKpiCardsProps {
  metrics: PrMetricsSummary;
  onFilterClick?: (type: string) => void;
}

export const PrKpiCards: React.FC<PrKpiCardsProps> = ({ metrics, onFilterClick }) => {
  const cards = [
    {
      title: 'Total PRs',
      value: metrics.totalPrs,
      subtitle: 'Active requisitions',
      icon: Layers,
      colorClass: 'text-primary bg-primary/10 border-primary/20',
      type: 'ALL',
    },
    {
      title: 'Pending Approval',
      value: metrics.pendingApproval,
      subtitle: 'Awaiting NFA sign-off',
      icon: Clock,
      colorClass: 'text-amber-600 bg-amber-500/10 border-amber-500/20',
      type: 'PENDING_APPROVAL',
    },
    {
      title: 'Approved PRs',
      value: metrics.approved,
      subtitle: 'Ready for SAP sync',
      icon: CheckCircle2,
      colorClass: 'text-emerald-600 bg-emerald-500/10 border-emerald-500/20',
      type: 'APPROVED',
    },
    {
      title: 'SAP Created',
      value: metrics.sapCreated,
      subtitle: 'Synced in SAP S/4HANA',
      icon: ShieldCheck,
      colorClass: 'text-purple-600 bg-purple-500/10 border-purple-500/20',
      type: 'SAP_CREATED',
    },
    {
      title: 'Validation Failed',
      value: metrics.validationFailed,
      subtitle: 'Requires re-mapping',
      icon: AlertTriangle,
      colorClass: 'text-rose-600 bg-rose-500/10 border-rose-500/20',
      type: 'VALIDATION_FAILED',
    },
    {
      title: 'High Value PRs',
      value: metrics.highValuePrs,
      subtitle: '≥ ₹5,00,000 threshold',
      icon: DollarSign,
      colorClass: 'text-blue-600 bg-blue-500/10 border-blue-500/20',
      type: 'HIGH_VALUE',
    },
  ];

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3.5 font-heading">
      {cards.map((card) => {
        const Icon = card.icon;

        return (
          <Card
            key={card.title}
            onClick={() => onFilterClick && onFilterClick(card.type)}
            className="rounded-2xl border border-border bg-card hover:border-primary/40 transition-all cursor-pointer shadow-2xs"
          >
            <CardContent className="p-4 space-y-2">
              <div className="flex items-center justify-between">
                <div className={`p-2 rounded-xl border ${card.colorClass}`}>
                  <Icon className="h-4 w-4" />
                </div>
              </div>

              <div>
                <span className="font-heading text-xl sm:text-2xl font-extrabold tracking-tight text-foreground block truncate">
                  {card.value}
                </span>
                <span className="text-xs font-bold text-foreground leading-tight block truncate">
                  {card.title}
                </span>
              </div>

              <p className="text-[10px] text-foreground-muted font-medium truncate pt-1 border-t border-border/40">
                {card.subtitle}
              </p>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};
